import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import TableFormWrapper from 'components/atoms/FormWrapper';
import Grid from '@mui/material/Grid';
import { StyledValueFormHeader } from 'pages/SystemSettingsPage/styles';
import { Box, Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ActionTypes } from 'state/actions/alert';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { IOptionalField } from 'models/settings';
import { Table } from 'components/molecules/Table';
import { FieldsTableColumns, mapFieldsToDataSource } from './helpers';

interface IOptionalFieldForm {
  [key: string]: IOptionalField;
}

export const SystemFieldsConfigurationPage = () => {
  const { t } = useTranslations();
  const { samePageAlert, clearAlert, setAlert } = useAlert();
  const navigate = useNavigate();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [notVisibleNames, setNotVisibleNames] = useState<string[]>([]);
  const { updateSystemFieldsConfigurationData, getFieldsOptional, fieldsOptional } =
    useSystemFieldsConfiguration();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<IOptionalFieldForm>();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    getFieldsOptional();
  }, []);

  useEffect(() => {
    if (fieldsOptional.length) {
      let values: IOptionalFieldForm = {};
      fieldsOptional.map((field) => {
        values[field.name] = {
          uniqueId: field.uniqueId,
          visible: field.visible,
          required: field.required,
        };
      });
      reset(values);
      setNotVisibleNames(
        fieldsOptional.filter((item) => !item.visible).map((item) => item.name),
      );
    }
  }, [fieldsOptional]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formChanged = (data: IOptionalFieldForm) => true;
  // formInitialValues?.eTraceEnabled !== formCurrentValues?.eTraceEnabled ||
  // formInitialValues?.iArmsEnabled !== formCurrentValues?.iArmsEnabled;

  const onSubmitForm = async (data: IOptionalFieldForm) => {
    if (!formChanged(data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }

    setSubmitLoading(true);
    await updateSystemFieldsConfigurationData(Object.values(data));
    setSubmitLoading(false);
  };

  const handleChangeVisibilityStatus = (name: string, status: boolean) => {
    if (status === false) {
      setValue(`${name}.required`, false);
      setNotVisibleNames((prev) => [...prev, name]);
    } else {
      setNotVisibleNames((prev) => prev.filter((item) => item !== name));
    }
  };
  const handleChangeRequiredStatus = (name: string, status: boolean) => {
    if (status === true && !getValues(`${name}.visible`)) {
      setValue(`${name}.required`, false);
      return true;
    }
    return false;
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('systemFieldsConfiguration.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('systemFieldsConfiguration.header')}</StyledPageTitle>
      <TableFormWrapper>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container size={{ xs: 12 }} sx={{ gap: 7.5 }}>
            <Grid container size={{ xs: 12 }} sx={{ rowGap: 2.5 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1">
                  {t('systemFieldsConfiguration.title.text')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
                <StyledValueFormHeader>
                  {t('systemFieldsConfiguration.legalEntityModuleFields.label')}
                </StyledValueFormHeader>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Table
                  columns={FieldsTableColumns({
                    handleChangeVisibilityStatus: (name: string, status: boolean) =>
                      handleChangeVisibilityStatus(name, status),
                    handleChangeRequiredStatus: (name: string, status: boolean) =>
                      handleChangeRequiredStatus(name, status),
                    notVisibleNames,
                    t,
                    control,
                    errors,
                  })}
                  dataSource={mapFieldsToDataSource(fieldsOptional)}
                />
              </Grid>
              {samePageAlert && (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mt: 1 }}>
                    <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
                  </Box>
                </Grid>
              )}
            </Grid>
            <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 1, mt: 1 }}>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('general.save.button')}
                  isLoading={isSubmitLoading}
                  id="save-button"
                  variant={EButtonVariants.contained}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  onClick={() => navigate(ERouteLinks.Administration)}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </TableFormWrapper>
    </>
  );
};
